import { Checkbox } from "@synthesis/shared-ui";
import { cn } from "@synthesis/shared-utils";

import {
  getApplicantTypeLabel,
  isIndividualApplicantType,
  SIGNER_TYPE_LABELS,
  type ApplicantDataFormValues,
} from "../../../create-application-page/create-application-page.lib";
import { useApplicationDictionariesContext } from "../../application-dictionaries-context";
import type { ApplicationDetailField } from "../../application-detail-page.lib";
import { DetailSection } from "../detail-section";

type ApplicantContentProps = {
  applicantData: ApplicantDataFormValues | null;
  className?: string;
};

type ApplicantDetailSection = {
  title: string;
  fields: ApplicationDetailField[];
};

const formatApplicantValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (typeof value === "boolean") {
    return value ? "Да" : "Нет";
  }

  return String(value);
};

const buildStringListFields = (label: string, values: string[] | undefined): ApplicationDetailField[] => {
  const filledValues = (values ?? []).map((value) => value.trim()).filter(Boolean);

  if (filledValues.length === 0) {
    return [{ label, value: "—" }];
  }

  return filledValues.map((value) => ({ label, value }));
};

const buildContractEmailFields = (
  applicantData: ApplicantDataFormValues | null,
  useSeparateContractEmailField: "useSeparateContractEmail" | "payerUseSeparateContractEmail",
  contractEmailsField: "contractEmails" | "payerContractEmails",
): ApplicationDetailField[] => {
  if (!applicantData?.[useSeparateContractEmailField]) {
    return [];
  }

  return buildStringListFields("E-mail для направления договора", applicantData[contractEmailsField]);
};

const buildApplicantDetailSections = (applicantData: ApplicantDataFormValues | null, applicantTypeLabel: string): ApplicantDetailSection[] => {
  const isIndividual = applicantData ? isIndividualApplicantType(applicantData.applicantType) : false;
  const isIndividualEntrepreneur = applicantData?.applicantType === "SOLE_PROPRIETOR";
  const sections: ApplicantDetailSection[] = [
    {
      title: "Регистрационные реквизиты",
      fields: [
        { label: "Тип заявителя / лица", value: applicantTypeLabel },
        { label: isIndividual ? "Полное ФИО" : "Полное наименование / ИП ФИО", value: formatApplicantValue(applicantData?.organization) },
        ...(isIndividual || isIndividualEntrepreneur
          ? [
              { label: "СНИЛС", value: formatApplicantValue(applicantData?.snils) },
              { label: "ИНН", value: formatApplicantValue(applicantData?.inn) },
              ...(isIndividualEntrepreneur ? [{ label: "ОГРН / ОГРНИП", value: formatApplicantValue(applicantData?.ogrn) }] : []),
            ]
          : [
              { label: "Сокращённое наименование", value: formatApplicantValue(applicantData?.shortOrganizationName) },
              { label: "ИНН", value: formatApplicantValue(applicantData?.inn) },
              { label: "ОГРН / ОГРНИП", value: formatApplicantValue(applicantData?.ogrn) },
              { label: "ОКВЭД", value: formatApplicantValue(applicantData?.okved) },
              { label: "ОКТМО / ОКАТО", value: formatApplicantValue(applicantData?.oktmoOkato) },
              { label: "ОКПО", value: formatApplicantValue(applicantData?.okpo) },
              { label: "КПП", value: formatApplicantValue(applicantData?.kpp) },
            ]),
      ],
    },
    {
      title: "Контактная информация",
      fields: [
        {
          label: isIndividual ? "Адрес регистрации" : "Юридический адрес / Адрес регистрации",
          value: formatApplicantValue(applicantData?.legalAddress),
        },
        ...(!isIndividual ? [{ label: "Фактический адрес", value: formatApplicantValue(applicantData?.actualAddress) }] : []),
        { label: "Почтовый адрес", value: formatApplicantValue(applicantData?.postalAddress) },
        ...buildStringListFields(isIndividual ? "Контактный телефон" : "Контактный телефон организации", applicantData?.phones),
        ...buildStringListFields(isIndividual ? "E-mail" : "E-mail организации", applicantData?.emails),
        ...(!isIndividual ? buildContractEmailFields(applicantData, "useSeparateContractEmail", "contractEmails") : []),
      ],
    },
  ];

  if (isIndividual) {
    return sections;
  }

  sections.push(
    {
      title: "Сведения о подписанте",
      fields: [
        { label: "Тип подписанта", value: applicantData ? SIGNER_TYPE_LABELS[applicantData.signerType] : "—" },
        { label: "ФИО подписанта", value: formatApplicantValue(applicantData?.signerFullName) },
        ...(applicantData?.signerType === "representative"
          ? [
              { label: "Должность подписанта", value: formatApplicantValue(applicantData?.signerPosition) },
              { label: "Основание полномочий", value: formatApplicantValue(applicantData?.signerAuthority) },
              { label: "Реквизиты основания", value: formatApplicantValue(applicantData?.signerAuthorityDetails) },
            ]
          : [{ label: "Основание полномочий", value: formatApplicantValue(applicantData?.signerAuthority) }]),
      ],
    },
    {
      title: "Банковские реквизиты",
      fields: [
        { label: "Банк", value: formatApplicantValue(applicantData?.bankName) },
        { label: "БИК", value: formatApplicantValue(applicantData?.bik) },
        { label: "Расчётный счёт", value: formatApplicantValue(applicantData?.settlementAccount) },
        { label: "Корреспондентский счёт", value: formatApplicantValue(applicantData?.correspondentAccount) },
      ],
    },
  );

  return sections;
};

export const ApplicantContent = ({ applicantData, className }: ApplicantContentProps) => {
  const { dictionaries } = useApplicationDictionariesContext();
  const applicantTypeLabel =
    dictionaries.applicantTypes.find((option) => option.value === applicantData?.applicantType)?.label ??
    getApplicantTypeLabel(applicantData?.applicantType);

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      {buildApplicantDetailSections(applicantData, applicantTypeLabel).map((section) => (
        <DetailSection key={section.title} title={section.title} fields={section.fields} />
      ))}
      {applicantData && !isIndividualApplicantType(applicantData.applicantType) ? (
        <Checkbox checked={applicantData?.paymentByAnotherOrganization ?? false} disabled label="Оплату производит другая организация" />
      ) : null}
    </div>
  );
};
export default ApplicantContent;
